import React, { useEffect, useMemo, useRef, useState } from 'react';

import type { ColorGroup, ImageSrcsets } from '../types';
import './ProductCarousel.css';

interface ProductCarouselProps {
    colorGroups: ColorGroup[];
    selectedColorGroupIdx: number;
    selectedAdditionalImageIdx: number | null;
    onSelectColorGroup: (idx: number) => void;
    productName: string;
}

interface CarouselImage {
    url: string;
    srcsets: ImageSrcsets | null;
    groupIdx: number;
    additionalIdx: number | null;
    key: string;
}

function buildImages(groups: ColorGroup[]): CarouselImage[] {
    const seen = new Set<string>();
    const images: CarouselImage[] = [];
    groups.forEach((group, groupIdx) => {
        if (group.image_url && !seen.has(group.image_url)) {
            seen.add(group.image_url);
            images.push({
                url: group.image_url,
                srcsets: group.image_srcsets,
                groupIdx,
                additionalIdx: null,
                key: `g${groupIdx}-primary`,
            });
        }
        group.additional_images.forEach((extra, additionalIdx) => {
            if (extra.url && !seen.has(extra.url)) {
                seen.add(extra.url);
                images.push({
                    url: extra.url,
                    srcsets: null,
                    groupIdx,
                    additionalIdx,
                    key: `g${groupIdx}-a${additionalIdx}`,
                });
            }
        });
    });
    return images;
}

function findInitialKey(
    images: CarouselImage[],
    groupIdx: number,
    additionalIdx: number | null,
): string | null {
    if (images.length === 0) { return null; }
    const match = images.find(
        (img) => img.groupIdx === groupIdx && img.additionalIdx === additionalIdx,
    );
    if (match) { return match.key; }
    const groupPrimary = images.find((img) => img.groupIdx === groupIdx);
    return (groupPrimary ?? images[0]).key;
}

export function ProductCarousel({
    colorGroups,
    selectedColorGroupIdx,
    selectedAdditionalImageIdx,
    onSelectColorGroup,
    productName,
}: ProductCarouselProps) {
    const images = useMemo(() => buildImages(colorGroups), [colorGroups]);
    const [activeKey, setActiveKey] = useState<string | null>(() =>
        findInitialKey(images, selectedColorGroupIdx, selectedAdditionalImageIdx),
    );

    const prevColorGroupIdxRef = useRef(selectedColorGroupIdx);
    useEffect(() => {
        if (prevColorGroupIdxRef.current === selectedColorGroupIdx) { return; }
        prevColorGroupIdxRef.current = selectedColorGroupIdx;
        const primary = images.find(
            (img) => img.groupIdx === selectedColorGroupIdx && img.additionalIdx === null,
        );
        const fallback = images.find((img) => img.groupIdx === selectedColorGroupIdx);
        const next = primary ?? fallback ?? null;
        if (next) { setActiveKey(next.key); }
    }, [selectedColorGroupIdx, images]);

    const activeImage = images.find((img) => img.key === activeKey) ?? images[0] ?? null;

    const handleThumbClick = (img: CarouselImage) => {
        setActiveKey(img.key);
        if (img.groupIdx !== selectedColorGroupIdx) {
            onSelectColorGroup(img.groupIdx);
        }
    };

    return (
        <section className="pdp-carousel" aria-label="Product images">
            <div className="pdp-carousel__main">
                {activeImage ? (
                    activeImage.srcsets ? (
                        <picture>
                            <source srcSet={activeImage.srcsets.avif} type="image/avif" />
                            <source srcSet={activeImage.srcsets.webp} type="image/webp" />
                            <img
                                src={activeImage.url}
                                alt={productName}
                                className="pdp-carousel__main-img"
                            />
                        </picture>
                    ) : (
                        <img src={activeImage.url} alt={productName} className="pdp-carousel__main-img" />
                    )
                ) : (
                    <div className="pdp-carousel__placeholder" aria-hidden="true" />
                )}
                {images.length > 1 && (
                    <div className="pdp-carousel__thumbs" role="tablist" aria-label="Image thumbnails">
                        {images.map((img) => {
                            const isActive = img.key === activeImage?.key;
                            return (
                                <button
                                    key={img.key}
                                    type="button"
                                    role="tab"
                                    aria-selected={isActive}
                                    aria-label={colorGroups[img.groupIdx]?.color || `Image ${img.groupIdx + 1}`}
                                    className={`pdp-carousel__thumb${isActive ? ' pdp-carousel__thumb--active' : ''}`}
                                    onClick={() => handleThumbClick(img)}
                                >
                                    <img src={img.url} alt="" loading="lazy" />
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>
        </section>
    );
}
